import { useEffect, useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";
import type { PlanConfig, ReportConfig, ReportFolder } from "../types";
import { AddActionButton } from "./AddActionButton";
import { DestructiveConfirmDialog } from "./DestructiveConfirmDialog";

type DeleteConfirmation =
    | { kind: "folder"; folder: ReportFolder }
    | { kind: "report"; folder: ReportFolder; report: ReportConfig };

interface ReportTreeProps {
    selectedPlan: PlanConfig;
    selectedFolder?: ReportFolder;
    selectedReport?: ReportConfig;
    expandedFolders: Set<string>;
    setExpandedFolders: Dispatch<SetStateAction<Set<string>>>;
    renameFolderId: string | null;
    setRenameFolderId: (folderId: string | null) => void;
    renameReportId: string | null;
    setRenameReportId: (reportId: string | null) => void;
    newReportNames: Record<string, string>;
    setNewReportNames: Dispatch<SetStateAction<Record<string, string>>>;
    newFolderName: string;
    setNewFolderName: (value: string) => void;
    replaceSelectedPlan: (updater: (plan: PlanConfig) => PlanConfig) => void;
    deleteFolder: (folderId: string) => boolean | Promise<boolean>;
    deleteReport: (folderId: string, reportId: string) => boolean | Promise<boolean>;
    addReport: (folderId: string) => void | Promise<void>;
    addFolder: () => void | Promise<void>;
    canAddFolder?: boolean;
    addFolderDisabledReason?: string;
    renameFolder: (folderId: string, label: string) => void | Promise<void>;
    renameReport: (folderId: string, reportId: string, label: string) => void | Promise<void>;
    requestReportNavigation: (folderId: string, reportId: string) => void;
    collapseNavigatorSignal?: number;
    openNavigatorSignal?: number;
    renderReportEditor: (report: ReportConfig) => ReactNode;
}

export function ReportTree({
    selectedPlan,
    selectedFolder,
    selectedReport,
    expandedFolders,
    setExpandedFolders,
    renameFolderId,
    setRenameFolderId,
    renameReportId,
    setRenameReportId,
    newReportNames,
    setNewReportNames,
    newFolderName,
    setNewFolderName,
    replaceSelectedPlan,
    deleteFolder,
    deleteReport,
    addReport,
    addFolder,
    canAddFolder = true,
    addFolderDisabledReason,
    renameFolder,
    renameReport,
    requestReportNavigation,
    collapseNavigatorSignal = 0,
    openNavigatorSignal = 0,
    renderReportEditor,
}: ReportTreeProps) {
    const [activeAddReportFolderId, setActiveAddReportFolderId] = useState<string | null>(null);
    const [addingFolder, setAddingFolder] = useState(false);
    const [navigatorCollapsed, setNavigatorCollapsed] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmation | null>(null);
    const [deleteConfirmationLoading, setDeleteConfirmationLoading] = useState(false);
    const [renameFolderOriginals, setRenameFolderOriginals] = useState<Record<string, string>>({});
    const [renameReportOriginals, setRenameReportOriginals] = useState<Record<string, string>>({});
    const cancelledFolderRenameIds = useRef(new Set<string>());
    const cancelledReportRenameIds = useRef(new Set<string>());

    const cancelAddReport = (folderId = activeAddReportFolderId) => {
        if (folderId) {
            setNewReportNames((current) => current[folderId] ? { ...current, [folderId]: "" } : current);
        }
        setActiveAddReportFolderId(null);
    };

    const cancelAddFolder = () => {
        setNewFolderName("");
        setAddingFolder(false);
    };

    useEffect(() => {
        if (collapseNavigatorSignal > 0) {
            cancelAddReport();
            cancelAddFolder();
            setNavigatorCollapsed((current) => current ? current : true);
        }
    }, [collapseNavigatorSignal]);

    useEffect(() => {
        if (openNavigatorSignal > 0) {
            setNavigatorCollapsed((current) => current ? false : current);
        }
    }, [openNavigatorSignal]);

    useEffect(() => {
        cancelAddReport();
        cancelAddFolder();
    }, [selectedFolder?.id, selectedReport?.id]);

    const beginAddReport = (folderId: string) => {
        cancelAddFolder();
        if (activeAddReportFolderId && activeAddReportFolderId !== folderId) {
            cancelAddReport(activeAddReportFolderId);
        }
        setActiveAddReportFolderId(folderId);
    };

    const toggleFolder = (folder: ReportFolder, shouldSelect = false) => {
        cancelAddReport(folder.id);
        cancelAddFolder();
        const isExpanded = expandedFolders.has(folder.id);
        setExpandedFolders(isExpanded ? new Set() : new Set([folder.id]));
        if (shouldSelect && !isExpanded) {
            requestReportNavigation(folder.id, folder.reports[0]?.id || "");
        }
    };

    const selectFolder = (folder: ReportFolder) => {
        toggleFolder(folder, true);
    };

    const selectReport = (folder: ReportFolder, report: ReportConfig) => {
        cancelAddReport();
        cancelAddFolder();
        requestReportNavigation(folder.id, report.id);
    };

    const startRenameFolder = (folder: ReportFolder) => {
        setRenameFolderOriginals((current) => ({ ...current, [folder.id]: folder.label }));
        setRenameFolderId(folder.id);
    };

    const finishRenameFolder = (folder: ReportFolder) => {
        const original = (renameFolderOriginals[folder.id] ?? folder.label).trim();
        const normalized = folder.label.trim() || original || "New Folder";
        setRenameFolderId(null);
        setRenameFolderOriginals((current) => {
            const next = { ...current };
            delete next[folder.id];
            return next;
        });
        if (cancelledFolderRenameIds.current.delete(folder.id)) return;
        if (normalized === original) {
            replaceSelectedPlan((plan) => ({
                ...plan,
                reportFolders: plan.reportFolders.map((item) => item.id === folder.id ? { ...item, label: original } : item),
            }));
            return;
        }
        void renameFolder(folder.id, normalized);
    };

    const cancelRenameFolder = (folder: ReportFolder) => {
        const original = renameFolderOriginals[folder.id] ?? folder.label;
        cancelledFolderRenameIds.current.add(folder.id);
        setRenameFolderId(null);
        setRenameFolderOriginals((current) => {
            const next = { ...current };
            delete next[folder.id];
            return next;
        });
        replaceSelectedPlan((plan) => ({
            ...plan,
            reportFolders: plan.reportFolders.map((item) => item.id === folder.id ? { ...item, label: original } : item),
        }));
    };

    const startRenameReport = (report: ReportConfig) => {
        setRenameReportOriginals((current) => ({ ...current, [report.id]: report.label }));
        setRenameReportId(report.id);
    };

    const finishRenameReport = (folder: ReportFolder, report: ReportConfig) => {
        const original = (renameReportOriginals[report.id] ?? report.label).trim();
        const normalized = report.label.trim() || original || "New Report";
        setRenameReportId(null);
        setRenameReportOriginals((current) => {
            const next = { ...current };
            delete next[report.id];
            return next;
        });
        if (cancelledReportRenameIds.current.delete(report.id)) return;
        if (normalized === original) {
            replaceSelectedPlan((plan) => ({
                ...plan,
                reportFolders: plan.reportFolders.map((item) => item.id === folder.id ? {
                    ...item,
                    reports: item.reports.map((currentReport) => currentReport.id === report.id ? { ...currentReport, label: original } : currentReport),
                } : item),
            }));
            return;
        }
        void renameReport(folder.id, report.id, normalized);
    };

    const cancelRenameReport = (folder: ReportFolder, report: ReportConfig) => {
        const original = renameReportOriginals[report.id] ?? report.label;
        cancelledReportRenameIds.current.add(report.id);
        setRenameReportId(null);
        setRenameReportOriginals((current) => {
            const next = { ...current };
            delete next[report.id];
            return next;
        });
        replaceSelectedPlan((plan) => ({
            ...plan,
            reportFolders: plan.reportFolders.map((item) => item.id === folder.id ? {
                ...item,
                reports: item.reports.map((currentReport) => currentReport.id === report.id ? { ...currentReport, label: original } : currentReport),
            } : item),
        }));
    };

    const confirmDelete = async () => {
        if (!deleteConfirmation || deleteConfirmationLoading) return;

        setDeleteConfirmationLoading(true);
        try {
            const deleted = deleteConfirmation.kind === "folder"
                ? await deleteFolder(deleteConfirmation.folder.id)
                : await deleteReport(deleteConfirmation.folder.id, deleteConfirmation.report.id);

            if (deleted) {
                setDeleteConfirmation(null);
            }
        } finally {
            setDeleteConfirmationLoading(false);
        }
    };

    const deleteSubject = deleteConfirmation?.kind === "folder"
        ? deleteConfirmation.folder.label
        : deleteConfirmation?.report.label || "";
    const deleteContext = deleteConfirmation?.kind === "folder"
        ? `${deleteConfirmation.folder.reports.length} ${deleteConfirmation.folder.reports.length === 1 ? "report" : "reports"}`
        : deleteConfirmation
            ? `Folder: ${deleteConfirmation.folder.label}`
            : undefined;
    const deleteImpact = !deleteConfirmation
        ? []
        : deleteConfirmation.kind === "folder"
            ? [
                `${deleteConfirmation.folder.reports.length} ${deleteConfirmation.folder.reports.length === 1 ? "report" : "reports"} in this folder will no longer be available here.`,
                "Saved report tree metadata for this health plan will be updated.",
            ]
            : [
                "This report will be removed from the folder list.",
                "Saved configuration versions tied to this report may no longer be reachable from this screen.",
            ];

    return (
        <div className={navigatorCollapsed ? "rc-report-workspace nav-collapsed" : "rc-report-workspace"}>
            <DestructiveConfirmDialog
                open={!!deleteConfirmation}
                kind={deleteConfirmation?.kind || "report"}
                subject={deleteSubject}
                context={deleteContext}
                impact={deleteImpact}
                loading={deleteConfirmationLoading}
                onCancel={() => !deleteConfirmationLoading && setDeleteConfirmation(null)}
                onConfirm={confirmDelete}
            />
            <aside className={navigatorCollapsed ? "rc-report-tree collapsed" : "rc-report-tree"}>
                {navigatorCollapsed ? (
                    <div className="rc-tree-rail" aria-label="Folders and Reports">
                        <button className="rc-tree-rail-toggle" type="button" title="Show Navigator" aria-label="Show Folders and Reports" onClick={() => setNavigatorCollapsed(false)}>
                            <span className="rc-tree-rail-toggle-orbit" aria-hidden="true">
                                <span className="material-icons md-18">account_tree</span>
                            </span>
                        </button>
                        <div className="rc-tree-rail-items">
                            {selectedPlan.reportFolders.map((folder) => {
                                const isActiveFolder = folder.id === selectedFolder?.id;
                                return (
                                    <button
                                        className={isActiveFolder ? "rc-tree-rail-item active" : "rc-tree-rail-item"}
                                        key={folder.id}
                                        type="button"
                                        aria-label={`Open ${folder.label}`}
                                        onClick={() => {
                                            if (folder.id === selectedFolder?.id) {
                                                setExpandedFolders(new Set([folder.id]));
                                            } else {
                                                selectFolder(folder);
                                            }
                                            setNavigatorCollapsed(false);
                                        }}
                                    >
                                        <span className="material-icons md-18" aria-hidden="true">{isActiveFolder ? "folder_open" : "folder"}</span>
                                        <span className="rc-tree-rail-badge">{folder.reports.length}</span>
                                        <span className="rc-tree-tooltip" role="tooltip">
                                            <strong>{folder.label}</strong>
                                            <small>{folder.reports.length} {folder.reports.length === 1 ? "report" : "reports"}</small>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="rc-tree-top">
                            <p className="rc-hint tree-title"><span className="material-icons md-16" aria-hidden="true">folder_open</span> Folders and Reports</p>
                            <button className="rc-tree-collapse-action" type="button" title="Collapse Navigator" aria-label="Collapse Folders and Reports" onClick={() => { cancelAddReport(); cancelAddFolder(); setNavigatorCollapsed(true); }}>
                                <span className="material-icons md-16" aria-hidden="true">keyboard_double_arrow_left</span>
                            </button>
                        </div>
                {selectedPlan.reportFolders.map((folder) => {
                    const isExpanded = expandedFolders.has(folder.id);
                    const isActiveFolder = folder.id === selectedFolder?.id;
                    const draftReportName = (newReportNames[folder.id] || "").trim();
                    return (
                        <div className={["rc-tree-folder", isExpanded ? "expanded" : "collapsed", isActiveFolder ? "active" : ""].filter(Boolean).join(" ")} key={folder.id}>
                            <div className={isActiveFolder ? "rc-folder-row active" : "rc-folder-row"}>
                                <button
                                    aria-label={isExpanded ? "Collapse Folder" : "Expand Folder"}
                                    aria-expanded={isExpanded}
                                    className="rc-folder-toggle"
                                    onClick={() => toggleFolder(folder)}
                                    type="button"
                                >
                                    <span className="material-icons md-14">{isExpanded ? "expand_more" : "chevron_right"}</span>
                                </button>
                                {renameFolderId === folder.id ? (
                                    <input
                                        autoFocus
                                        value={folder.label}
                                        onBlur={() => finishRenameFolder(folder)}
                                        onChange={(event) => replaceSelectedPlan((plan) => ({ ...plan, reportFolders: plan.reportFolders.map((item) => item.id === folder.id ? { ...item, label: event.target.value } : item) }))}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter") event.currentTarget.blur();
                                            if (event.key === "Escape") cancelRenameFolder(folder);
                                        }}
                                    />
                                ) : (
                                    <button className="rc-folder-name" title={folder.label} onClick={() => selectFolder(folder)}>
                                        <span className="material-icons md-16" aria-hidden="true">{isExpanded ? "folder_open" : "folder"}</span>
                                        <span className="rc-node-label">{folder.label}</span>
                                        <span className="rc-node-count">{folder.reports.length}</span>
                                    </button>
                                )}
                                {isActiveFolder && (
                                    <div className="rc-node-actions">
                                        <button className="rc-iconbtn" type="button" aria-label={`Rename ${folder.label}`} title="Rename Folder" onClick={() => startRenameFolder(folder)}>
                                            <span className="material-icons md-16" aria-hidden="true">edit</span>
                                        </button>
                                        <button className="rc-iconbtn muted" type="button" aria-label={`Delete ${folder.label}`} disabled={folder.isBuiltIn} title={folder.isBuiltIn ? "Built-In Folders Cannot Be Deleted" : "Delete Folder"} onClick={() => setDeleteConfirmation({ kind: "folder", folder })}>
                                            <span className="material-icons md-16" aria-hidden="true">delete</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                            {isExpanded && (
                                <div className="rc-report-list">
                                    {folder.reports.map((report) => (
                                        <div className={report.id === selectedReport?.id ? "rc-report-row active" : "rc-report-row"} key={report.id}>
                                            {renameReportId === report.id ? (
                                                <input
                                                    autoFocus
                                                    value={report.label}
                                                    onBlur={() => finishRenameReport(folder, report)}
                                                    onChange={(event) => replaceSelectedPlan((plan) => ({ ...plan, reportFolders: plan.reportFolders.map((item) => item.id === folder.id ? { ...item, reports: item.reports.map((rep) => rep.id === report.id ? { ...rep, label: event.target.value } : rep) } : item) }))}
                                                    onKeyDown={(event) => {
                                                        if (event.key === "Enter") event.currentTarget.blur();
                                                        if (event.key === "Escape") cancelRenameReport(folder, report);
                                                    }}
                                                />
                                            ) : (
                                                <button className="rc-report-name" title={report.label} onClick={() => selectReport(folder, report)}>
                                                    <span className="material-icons md-16" aria-hidden="true">description</span>
                                                    <span className="rc-node-label">{report.label}</span>
                                                </button>
                                            )}
                                            {report.id === selectedReport?.id && (
                                                <div className="rc-node-actions">
                                                    <button className="rc-iconbtn" type="button" aria-label={`Rename ${report.label}`} title="Rename Report" onClick={() => startRenameReport(report)}>
                                                        <span className="material-icons md-16" aria-hidden="true">edit</span>
                                                    </button>
                                                    <button className="rc-iconbtn muted" type="button" aria-label={`Delete ${report.label}`} disabled={report.isBuiltIn} title={report.isBuiltIn ? "Built-In Reports Cannot Be Deleted" : "Delete Report"} onClick={() => setDeleteConfirmation({ kind: "report", folder, report })}>
                                                        <span className="material-icons md-16" aria-hidden="true">delete</span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    <div className="rc-folder-footer">
                                        {activeAddReportFolderId === folder.id ? (
                                            <div className="rc-add-report-row active">
                                                <input autoFocus value={newReportNames[folder.id] || ""} onChange={(event) => setNewReportNames((current) => ({ ...current, [folder.id]: event.target.value }))} placeholder="Report Name" onKeyDown={(event) => {
                                                    if (event.key === "Enter" && event.currentTarget.value.trim()) {
                                                        void addReport(folder.id);
                                                        setActiveAddReportFolderId(null);
                                                    }
                                                    if (event.key === "Escape") cancelAddReport(folder.id);
                                                }} />
                                                <button className="rc-minibtn icon-only" type="button" aria-label={`Create Report in ${folder.label}`} disabled={!draftReportName} title={draftReportName ? "Create Report" : "Enter a report name"} onClick={() => { if (!draftReportName) return; void addReport(folder.id); setActiveAddReportFolderId(null); }}>
                                                    <span className="material-icons md-16" aria-hidden="true">check</span>
                                                </button>
                                                <button className="rc-minibtn icon-only" type="button" aria-label={`Cancel adding report in ${folder.label}`} title="Cancel" onClick={() => cancelAddReport(folder.id)}>
                                                    <span className="material-icons md-16" aria-hidden="true">close</span>
                                                </button>
                                            </div>
                                        ) : (
                                            <AddActionButton className="full rc-add-node" onClick={() => beginAddReport(folder.id)}>Add Report</AddActionButton>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
                {addingFolder ? (
                    <div className="rc-add-report-row rc-add-folder-row active">
                        <input autoFocus disabled={!canAddFolder} value={newFolderName} onChange={(event) => setNewFolderName(event.target.value)} placeholder="Folder Name" onKeyDown={(event) => {
                            if (event.key === "Enter" && canAddFolder && event.currentTarget.value.trim()) {
                                void addFolder();
                                setAddingFolder(false);
                            }
                            if (event.key === "Escape") cancelAddFolder();
                        }} />
                        <button className="rc-minibtn icon-only" type="button" aria-label="Create Folder" disabled={!canAddFolder || !newFolderName.trim()} title={!canAddFolder ? addFolderDisabledReason : newFolderName.trim() ? "Create Folder" : "Enter a folder name"} onClick={() => { if (!canAddFolder || !newFolderName.trim()) return; void addFolder(); setAddingFolder(false); }}>
                            <span className="material-icons md-16" aria-hidden="true">check</span>
                        </button>
                        <button className="rc-minibtn icon-only" type="button" aria-label="Cancel adding folder" title="Cancel" onClick={cancelAddFolder}>
                            <span className="material-icons md-16" aria-hidden="true">close</span>
                        </button>
                        {!canAddFolder && addFolderDisabledReason && <p className="rc-hint">{addFolderDisabledReason}</p>}
                    </div>
                ) : (
                    <AddActionButton className="full root rc-add-node" icon="create_new_folder" disabled={!canAddFolder} title={!canAddFolder ? addFolderDisabledReason : undefined} onClick={() => { cancelAddReport(); setAddingFolder(true); }}>Add Folder</AddActionButton>
                )}
                    </>
                )}
            </aside>
            <div className="rc-editor">{selectedReport ? renderReportEditor(selectedReport) : <p className="rc-hint">Select a report on the left, or add a new one to "{selectedFolder?.label}".</p>}</div>
        </div>
    );
}
